/**
 * Template Management Component
 * Allows users to create, edit, and manage document templates
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  Copy,
  Archive,
  RotateCcw,
  Search,
  Loader2,
  X,
  Check,
  AlertTriangle,
  Code
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { templateService } from '../../../services/api/documentGeneratorService';
import {
  DocumentTemplate,
  DocumentCategory,
  Placeholder,
  PlaceholderType,
  DOCUMENT_CATEGORY_LABELS
} from '../../../types/documentGenerator.types';

interface TemplateManagementProps {
  onSelectTemplate?: (template: DocumentTemplate) => void;
}

// Category options for dropdown
const categoryOptions: { value: DocumentCategory; label: string }[] = [
  { value: 'nda', label: 'Non-Disclosure Agreement' },
  { value: 'loa', label: 'Letter of Authorization' },
  { value: 'loc', label: 'Letter of Confirmation' },
  { value: 'employment', label: 'Employment Contract' },
  { value: 'internship', label: 'Internship Agreement' },
  { value: 'confidentiality', label: 'Confidentiality Agreement' },
  { value: 'compliance', label: 'Compliance Document' },
  { value: 'delegation', label: 'Delegation of Authority' },
  { value: 'policy', label: 'Policy Document' },
  { value: 'other', label: 'Other' }
];

// Placeholder type options
const placeholderTypeOptions: { value: PlaceholderType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'select', label: 'Dropdown' }
];

// Default template form data
const defaultFormData = {
  name: '',
  category: 'other' as DocumentCategory,
  description: '',
  content: '',
  placeholders: [] as Placeholder[],
  tags: [] as string[],
  requiresApproval: false
};

export const TemplateManagement: React.FC<TemplateManagementProps> = ({ onSelectTemplate }) => {
  const { token } = useAuth();

  // State
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | 'all'>('all');
  const [showArchived, setShowArchived] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplate | null>(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Placeholder editor state
  const [showPlaceholderEditor, setShowPlaceholderEditor] = useState(false);
  const [editingPlaceholder, setEditingPlaceholder] = useState<Placeholder | null>(null);
  const [placeholderForm, setPlaceholderForm] = useState<Placeholder>({
    key: '',
    label: '',
    type: 'text',
    required: false,
    defaultValue: '',
    options: [],
    helpText: ''
  });

  // Tag input state
  const [tagInput, setTagInput] = useState('');

  // Load templates
  const loadTemplates = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await templateService.getAll(token, {
        isActive: !showArchived,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        search: searchTerm || undefined
      });
      setTemplates(response.data);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  }, [token, showArchived, categoryFilter, searchTerm]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Modal handlers
  const handleOpenModal = (template?: DocumentTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        category: template.category,
        description: template.description || '',
        content: template.content,
        placeholders: template.placeholders || [],
        tags: template.tags || [],
        requiresApproval: template.requiresApproval || false
      });
    } else {
      setEditingTemplate(null);
      setFormData(defaultFormData);
    }
    setError(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setFormData(defaultFormData);
    setError(null);
  };

  // Save template
  const handleSave = async () => {
    if (!token) return;
    if (!formData.name.trim()) {
      setError('Template name is required');
      return;
    }
    if (!formData.content.trim()) {
      setError('Template content is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editingTemplate) {
        await templateService.update(token, editingTemplate._id, {
          name: formData.name.trim(),
          category: formData.category,
          description: formData.description.trim(),
          content: formData.content,
          placeholders: formData.placeholders,
          tags: formData.tags,
          requiresApproval: formData.requiresApproval
        });
      } else {
        await templateService.create(token, {
          name: formData.name.trim(),
          category: formData.category,
          description: formData.description.trim(),
          content: formData.content,
          placeholders: formData.placeholders,
          tags: formData.tags,
          requiresApproval: formData.requiresApproval
        });
      }
      await loadTemplates();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const handleDelete = async (template: DocumentTemplate) => {
    if (!token) return;
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      await templateService.delete(token, template._id);
      await loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
    }
  };

  // Archive/Restore template
  const handleArchive = async (template: DocumentTemplate) => {
    if (!token) return;
    try {
      await templateService.archive(token, template._id);
      await loadTemplates();
    } catch (err) {
      console.error('Error archiving template:', err);
    }
  };

  const handleRestore = async (template: DocumentTemplate) => {
    if (!token) return;
    try {
      await templateService.restore(token, template._id);
      await loadTemplates();
    } catch (err) {
      console.error('Error restoring template:', err);
    }
  };

  // Clone template
  const handleClone = async (template: DocumentTemplate) => {
    if (!token) return;
    try {
      await templateService.clone(token, template._id, `${template.name} (Copy)`);
      await loadTemplates();
    } catch (err) {
      console.error('Error cloning template:', err);
    }
  };

  // Placeholder handlers
  const handleAddPlaceholder = () => {
    setEditingPlaceholder(null);
    setPlaceholderForm({
      key: '',
      label: '',
      type: 'text',
      required: false,
      defaultValue: '',
      options: [],
      helpText: ''
    });
    setShowPlaceholderEditor(true);
  };

  const handleEditPlaceholder = (placeholder: Placeholder) => {
    setEditingPlaceholder(placeholder);
    setPlaceholderForm({ ...placeholder });
    setShowPlaceholderEditor(true);
  };

  const handleSavePlaceholder = () => {
    if (!placeholderForm.key.trim() || !placeholderForm.label.trim()) {
      return;
    }

    const newPlaceholder: Placeholder = {
      ...placeholderForm,
      key: placeholderForm.key.replace(/\s+/g, '_').toUpperCase()
    };

    if (editingPlaceholder) {
      setFormData(prev => ({
        ...prev,
        placeholders: prev.placeholders.map(p =>
          p.key === editingPlaceholder.key ? newPlaceholder : p
        )
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        placeholders: [...prev.placeholders, newPlaceholder]
      }));
    }

    setShowPlaceholderEditor(false);
    setEditingPlaceholder(null);
  };

  const handleDeletePlaceholder = (key: string) => {
    setFormData(prev => ({
      ...prev,
      placeholders: prev.placeholders.filter(p => p.key !== key)
    }));
  };

  // Insert placeholder into content
  const insertPlaceholderIntoContent = (key: string) => {
    const placeholder = `{{${key}}}`;
    setFormData(prev => ({
      ...prev,
      content: prev.content + placeholder
    }));
  };

  // Tag handlers
  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = searchTerm === '' ||
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Document Templates</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Create and manage templates for generating documents. Use placeholders like {'{{COMPANY_NAME}}'} for dynamic content.
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-accent-600 dark:bg-accent-500 text-white dark:text-gray-900 rounded-lg hover:bg-accent-700 dark:hover:bg-accent-400 flex items-center gap-2 font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Template
          </button>
        </div>

        {/* Search and Filter Row */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as DocumentCategory | 'all')}
              className="px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400"
            >
              <option value="all">All Categories</option>
              {categoryOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`px-3 py-2 border rounded-lg transition-colors ${
                showArchived
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300'
                  : 'bg-white dark:bg-dark-bg-secondary border-gray-300 dark:border-dark-border text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
              }`}
            >
              <Archive className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-8 text-center">
          <Loader2 className="w-8 h-8 text-primary-600 dark:text-accent-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading templates...</p>
        </div>
      ) : filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => (
            <div
              key={template._id}
              className={`bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 hover:shadow-md transition-shadow ${
                template.isArchived ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white line-clamp-1">
                      {template.name}
                    </h4>
                    <span className="text-xs text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-2 py-0.5 rounded-full">
                      {DOCUMENT_CATEGORY_LABELS[template.category]}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleOpenModal(template)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary rounded transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleClone(template)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary rounded transition-colors"
                    title="Clone"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  {template.isArchived ? (
                    <button
                      onClick={() => handleRestore(template)}
                      className="p-1.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary rounded transition-colors"
                      title="Restore"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleArchive(template)}
                      className="p-1.5 text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary rounded transition-colors"
                      title="Archive"
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(template)}
                    className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {template.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {template.description}
                </p>
              )}

              {/* Tags */}
              {template.tags && template.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {template.tags.slice(0, 3).map(tag => (
                    <span
                      key={tag}
                      className="text-xs bg-gray-100 dark:bg-dark-bg-secondary text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                  {template.tags.length > 3 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      +{template.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-dark-border text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  {template.placeholders?.length || 0} placeholders
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  v{template.version || '1.0'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-surface rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-8 text-left">
          <FileText className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {showArchived ? 'No Archived Templates' : 'No Templates Yet'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {showArchived
              ? 'No archived templates found. Archive templates to move them here.'
              : 'Create your first template to start generating documents.'}
          </p>
          {!showArchived && (
            <button
              onClick={() => handleOpenModal()}
              className="px-4 py-2 bg-accent-600 dark:bg-accent-500 text-white dark:text-gray-900 rounded-lg hover:bg-accent-700 dark:hover:bg-accent-400 inline-flex items-center gap-2 font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create First Template
            </button>
          )}
        </div>
      )}

      {/* Template Editor Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Standard NDA Template"
                      className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as DocumentCategory }))}
                      className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400"
                    >
                      {categoryOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of this template..."
                      rows={3}
                      className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-400 resize-none"
                    />
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tags
                    </label>
                    <div className="flex gap-2 mb-2 flex-wrap">
                      {formData.tags.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded text-sm"
                        >
                          {tag}
                          <button
                            onClick={() => handleRemoveTag(tag)}
                            className="hover:text-primary-900 dark:hover:text-primary-100"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                        placeholder="Add tag..."
                        className="flex-1 px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg text-sm"
                      />
                      <button
                        onClick={handleAddTag}
                        className="px-3 py-2 bg-gray-100 dark:bg-dark-bg-tertiary text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-border"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Options */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="requiresApproval"
                      checked={formData.requiresApproval}
                      onChange={(e) => setFormData(prev => ({ ...prev, requiresApproval: e.target.checked }))}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="requiresApproval" className="text-sm text-gray-700 dark:text-gray-300">
                      Requires approval before finalization
                    </label>
                  </div>
                </div>

                {/* Right Column - Placeholders */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Placeholders
                    </label>
                    <button
                      onClick={handleAddPlaceholder}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add Placeholder
                    </button>
                  </div>

                  {formData.placeholders.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-auto">
                      {formData.placeholders.map(placeholder => (
                        <div
                          key={placeholder.key}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <Code className="w-4 h-4 text-gray-400" />
                            <div>
                              <div className="font-mono text-sm text-gray-900 dark:text-white">
                                {`{{${placeholder.key}}}`}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {placeholder.label} • {placeholder.type}
                                {placeholder.required && <span className="text-red-500"> *</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => insertPlaceholderIntoContent(placeholder.key)}
                              className="p-1.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded"
                              title="Insert into content"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditPlaceholder(placeholder)}
                              className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeletePlaceholder(placeholder.key)}
                              className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg text-center text-sm text-gray-500 dark:text-gray-400">
                      No placeholders defined. Add placeholders to make your template dynamic.
                    </div>
                  )}

                  {/* Placeholder Editor */}
                  {showPlaceholderEditor && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Key (no spaces)
                          </label>
                          <input
                            type="text"
                            value={placeholderForm.key}
                            onChange={(e) => setPlaceholderForm(prev => ({
                              ...prev,
                              key: e.target.value.replace(/\s+/g, '_').toUpperCase()
                            }))}
                            placeholder="COMPANY_NAME"
                            className="w-full px-2 py-1.5 bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded text-sm font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Label
                          </label>
                          <input
                            type="text"
                            value={placeholderForm.label}
                            onChange={(e) => setPlaceholderForm(prev => ({ ...prev, label: e.target.value }))}
                            placeholder="Company Name"
                            className="w-full px-2 py-1.5 bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Type
                          </label>
                          <select
                            value={placeholderForm.type}
                            onChange={(e) => setPlaceholderForm(prev => ({ ...prev, type: e.target.value as PlaceholderType }))}
                            className="w-full px-2 py-1.5 bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded text-sm"
                          >
                            {placeholderTypeOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Default Value
                          </label>
                          <input
                            type="text"
                            value={placeholderForm.defaultValue || ''}
                            onChange={(e) => setPlaceholderForm(prev => ({ ...prev, defaultValue: e.target.value }))}
                            placeholder="Optional default"
                            className="w-full px-2 py-1.5 bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="placeholderRequired"
                          checked={placeholderForm.required}
                          onChange={(e) => setPlaceholderForm(prev => ({ ...prev, required: e.target.checked }))}
                          className="w-4 h-4"
                        />
                        <label htmlFor="placeholderRequired" className="text-xs text-gray-600 dark:text-gray-400">
                          Required field
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowPlaceholderEditor(false)}
                          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSavePlaceholder}
                          disabled={!placeholderForm.key || !placeholderForm.label}
                          className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                        >
                          {editingPlaceholder ? 'Update' : 'Add'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Template Content */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Template Content *
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Use {'{{PLACEHOLDER_KEY}}'} syntax for dynamic content. Click the + button next to placeholders to insert them.
                </p>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder={`NON-DISCLOSURE AGREEMENT

This Non-Disclosure Agreement ("Agreement") is entered into as of {{EFFECTIVE_DATE}} by and between:

{{DISCLOSING_PARTY_NAME}}, a company organized under the laws of {{JURISDICTION}} ("Disclosing Party"), and

{{RECEIVING_PARTY_NAME}}, ("Receiving Party").

1. PURPOSE
The Disclosing Party wishes to disclose certain confidential information to the Receiving Party for the purpose of {{PURPOSE}}.

...`}
                  rows={12}
                  className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-400 font-mono text-sm resize-y"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-secondary">
              <button
                onClick={handleCloseModal}
                disabled={saving}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.name.trim() || !formData.content.trim()}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {editingTemplate ? 'Save Changes' : 'Create Template'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManagement;
